<template>
    <b-overlay
        variant="white"
        :show="showLoading"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <b-card no-body :border-variant="borderVariant" :class="cardClass">
            <slot name="header">
                <div class="card-header">
                    <b-col md="6" class="pl-0">
                        <b-card-title>
                            <slot name="header-label"/>
                        </b-card-title>
                    </b-col>
                    <b-col md="6" class="pr-0">
                        <div class="float-right">
                            <slot name="header-controls"/>
                        </div>
                    </b-col>
                </div>
            </slot>
            <Transition mode="out-in" name="fade" v-if="search">
                <div :class="tableContainerClass" :style="tableContainerStyle">
                    <table class="table b-table table-hover table-striped bordered">
                        <thead>
                        <tr>
                            <th v-for="column in columns" :class="column.thClass || ''">
                                <slot :name="'header-cell-' + column.key" :column="column">
                                    <div :class="column.class || ''" :title="column.title">{{ column.label }}</div>
                                </slot>
                            </th>
                        </tr>
                        </thead>
                        <tbody v-if="search">
                            <tr>
                                <td style="white-space: nowrap;" v-for="column in columns">
                                    <slot :name="'filter-cell-' + column.key" :column="column" />
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-if="items.length">
                            <tr v-for="item in items" @click="onRowClick(item)">
                                <td v-for="column in columns" :class="getColumnClass(column, item)">
                                    <slot :name="'cell-' + column.key" :item="item" :column="column">
                                        {{ renderValue(item, column) }}
                                    </slot>
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr class="bg-white">
                                <td :colspan="columns.length">
                                    <b-alert
                                        class="m-2"
                                        variant="secondary"
                                        show
                                    >
                                        <div class="alert-body">
                                            <slot name="no-records">
                                                {{ $t('Component(Text):nothing-found') }}
                                            </slot>
                                        </div>
                                    </b-alert>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </Transition>
            <Transition mode="out-in" name="fade" v-else>
                <div v-if="items.length" :class="tableContainerClass" :style="tableContainerStyle">
                    <table class="table b-table table-hover table-striped bordered">
                        <thead>
                            <tr>
                                <th v-for="column in columns">
                                    <slot :name="'header-cell-' + column.key" :column="column">
                                        <div :class="column.class || ''" :title="column.title">{{ column.label }}</div>
                                    </slot>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in items" @click="onRowClick(item)">
                                <td v-for="column in columns" :class="getColumnClass(column, item)">
                                    <slot :name="'cell-' + column.key" :item="item" :column="column">
                                        {{ renderValue(item, column) }}
                                    </slot>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <b-alert
                    v-else-if="showEmptyMessage"
                    class="m-2"
                    variant="secondary"
                    show
                >
                    <div class="alert-body">
                        <slot name="no-records">
                            {{ $t('Component(Text):nothing-found') }}
                        </slot>
                    </div>
                </b-alert>
                <div v-else/>
            </Transition>
            <div v-if="showPaging" class="pl-2 pr-2 pb-2 d-flex justify-content-between flex-wrap">
                <div class="d-flex align-items-center justify-content-center">
                    <div>
                        {{ $t('Component(Grid):records-found', {total: totalItems}) }}
                    </div>
                    <div class="pl-1 pr-1">
                        <b-form-select
                            id="perPageSelect"
                            v-model="perPage"
                            @input="onPerPageChange"
                            inline
                            :options="pageOptions"
                        />
                    </div>
                    <div>{{ $t('Component(Grid):records-per-page') }}</div>
                </div>

                <div>
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="totalItems"
                        :per-page="perPage"
                        @input="onPageChange"
                        first-number
                        last-number
                        prev-class="prev-item"
                        next-class="next-item"
                        class="mb-0"
                    >
                        <template #prev-text>
                            <feather-icon
                                icon="ChevronLeftIcon"
                                size="18"
                            />
                        </template>
                        <template #next-text>
                            <feather-icon
                                icon="ChevronRightIcon"
                                size="18"
                            />
                        </template>
                    </b-pagination>
                </div>
            </div>
        </b-card>
    </b-overlay>
</template>

<script>
import {
    BCard, BCol, BOverlay, BCardTitle, BFormGroup, BPagination, BFormSelect, BAlert
} from 'bootstrap-vue'

export default {
    components: {
        BCard, BCol, BFormSelect, BOverlay, BAlert,
        BCardTitle, BFormGroup, BPagination
    },
    props: {
        borderVariant: {
            default: ''
        },
        cardClass: {
            default: ''
        },
        tdClassProvider: {
            default: null
        },
        tableContainerClass: {
            default: 'table-responsive hamlet-table',
        },
        tableContainerStyle: {
            default: ''
        },
        search: {
            default: false
        },
        columns: {
            type: Array,
            default() {
                return [];
            }
        },
        fetch: {
            default() {
                return () => {
                    this.showLoading = false;
                };
            }
        },
        pageOptions: {
            type: Array,
            default() {
                return [5, 10, 15, 30];
            }
        },
        defaultPerPage: {
            type: Number,
            default: 10,
        },
        showPagingWhenEmpty: {
            default: true
        },
        showEmptyMessage: {
            default: true
        },
        paging: {
            type: Boolean,
            default: true,
        },
        defaultFilters: {
            type: Object,
            default() {
                return {};
            }
        }
    },
    data() {
        return {
            currentPage: 1,
            perPage: this.defaultPerPage,
            totalItems: 0,
            items: [],
            showLoading: false,
            filters: structuredClone(this.defaultFilters),
        };
    },
    methods: {
        renderValue(item, column) {
            if (item.hasOwnProperty(column.key)) {
                if (item[column.key] instanceof Object && item[column.key].hasOwnProperty('title')) {
                    return item[column.key].title;
                }

                return item[column.key];
            }

            return '';
        },
        getColumnClass(column, item) {
            if (this.tdClassProvider) {
                return (this.tdClassProvider)(column, item);
            }
            return column.tdClass;
        },
        onPerPageChange() {
            this.$emit('per-page-change', this.perPage);
            this.makeRequest();
        },
        onPageChange() {
            this.makeRequest();
        },
        makeRequest() {
            this.showLoading = true;
            this.fetch(this.getConfig(), this.fetchHandler);
        },
        showLoader() {
            this.showLoading = true;
        },
        onRowClick(item) {
            this.$emit('onRowClick', item);
        },
        filter(key, value) {
            if (null === value || undefined === value || '' === value) {
                if (this.filters.hasOwnProperty(key)) {
                    delete this.filters[key];
                }
            } else {
                this.filters[key] = value;
            }
            this.makeRequest();
        },
        unsetFilter(key) {
            delete this.filters[key];
            this.makeRequest();
        },
        getConfig() {
            return {
                filters: this.filters,
                page: this.currentPage,
                perPage: this.perPage
            };
        },
        fetchHandler(totalItems, items) {
            this.totalItems = totalItems;
            this.items = items;
            this.showLoading = false;
        },
    },
    computed: {
        showPaging() {
            if (!this.paging) {
                return false;
            }
            if (this.items.length > 0) {
                return true;
            }

            return this.showPagingWhenEmpty;
        }
    },
    created() {
        this.$emit('created', this.getConfig(), this.fetchHandler, this.showLoader);
        this.makeRequest();
    },
}
</script>

<style lang="scss">
.hamlet-table {
    table {
        td,th {
            white-space: nowrap;
        }
    }
}
</style>
